import React, { useState, useEffect, useMemo } from "react";
import {
	Box,
	Typography,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Button,
	LinearProgress,
	Skeleton,
	Chip,
	IconButton,
	Avatar,
	Stack,
	Alert,
	Tooltip,
	Collapse,
	Divider,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	Tabs,
	Tab,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Grid,
	InputAdornment,
} from "@mui/material";
import {
	User,
	Clock,
	CheckCircle,
	Send,
	PlayCircle,
	Trophy,
	Target,
	ChevronDown,
	ChevronUp,
	Mail,
	MailOpen,
	MessageSquare,
	Calendar,
	Search,
	Filter,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useSelectedAcademicYear } from "../../hooks/useSelectedAcademicYear";
import AcademicYearSelector from "../../components/common/AcademicYearSelector";
import SendNotificationModal from "../../components/notifications/SendNotificationModal";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const CHUNK_SIZE = 100;

const chunkArray = (items = [], size = CHUNK_SIZE) => {
	const chunks = [];
	for (let i = 0; i < items.length; i += size) {
		chunks.push(items.slice(i, i + size));
	}
	return chunks;
};

const FunmathAdminReport = () => {
	const { supabase, user } = useAuth();
	const { selectedAcademicYear, setSelectedAcademicYear } =
		useSelectedAcademicYear();

	const [teacherData, setTeacherData] = useState([]);
	const [courseProgress, setCourseProgress] = useState([]);
	const [categories, setCategories] = useState([]);
	const [grades, setGrades] = useState({});
	const [loading, setLoading] = useState(true);
	const [fetchError, setFetchError] = useState("");
	const [expandedRows, setExpandedRows] = useState(new Set());
	const [userProfile, setUserProfile] = useState(null);
	const [userSchoolIds, setUserSchoolIds] = useState([]);
	const [teacherMessages, setTeacherMessages] = useState({});
	const [loadingMessages, setLoadingMessages] = useState({});
	const [expandedTabValues, setExpandedTabValues] = useState({});
	const [schools, setSchools] = useState([]);

	// Filter states
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedSchool, setSelectedSchool] = useState("all");

	// Notification modal state
	const [openNotificationModal, setOpenNotificationModal] = useState(false);
	const [selectedRecipient, setSelectedRecipient] = useState(null);

	// Define allowed roles
	const ALLOWED_ROLES = ["ROLE_FUNMATH_ADMIN", "ROLE_FUNMIYO_EMPLOYEE"];

	useEffect(() => {
		checkUserRole();
	}, [user]);

	useEffect(() => {
		if (userProfile?.role && ALLOWED_ROLES.includes(userProfile.role)) {
			fetchUserSchools();
		}
	}, [userProfile]);

	useEffect(() => {
		if (userSchoolIds.length > 0) {
			fetchAllData();
		}
	}, [userSchoolIds, selectedAcademicYear]);

	const checkUserRole = async () => {
		try {
			if (!user) return;

			const { data, error } = await supabase
				.from("profiles")
				.select("id, role, full_name, email")
				.eq("id", user.id)
				.single();

			if (error) throw error;
			setUserProfile(data);

			if (!ALLOWED_ROLES.includes(data.role)) {
				setLoading(false);
			}
		} catch (error) {
			console.error("Error checking user role:", error);
			setLoading(false);
		}
	};

	const fetchUserSchools = async () => {
		try {
			if (!user) return;

			if (userProfile?.role === "ROLE_FUNMATH_ADMIN") {
				const { data: allSchools, error: schoolsError } = await supabase
					.from("schools")
					.select("id")
					.eq("status", "Active");

				if (schoolsError) throw schoolsError;

				const allSchoolIds = (allSchools || []).map((school) => school.id);
				setUserSchoolIds(allSchoolIds);

				if (allSchoolIds.length === 0) {
					setTeacherData([]);
					setLoading(false);
				}
				return;
			}

			// Funmath experts: active employee_schools assignments only
			const { data: employeeSchools, error: employeeError } = await supabase
				.from("employee_schools")
				.select("school_id")
				.eq("profile_id", user.id)
				.eq("active_status", true);

			if (employeeError) throw employeeError;

			const allSchoolIds = [
				...new Set((employeeSchools || []).map((es) => es.school_id)),
			].filter(Boolean);

			setUserSchoolIds(allSchoolIds);

			if (allSchoolIds.length === 0) {
				setTeacherData([]);
				setLoading(false);
			}
		} catch (error) {
			console.error("Error fetching user schools:", error);
			setLoading(false);
		}
	};

	const fetchAllData = async () => {
		setLoading(true);
		setFetchError("");
		try {
			const schoolChunks = chunkArray(userSchoolIds);
			const schoolsData = [];

			for (const schoolChunk of schoolChunks) {
				const { data, error } = await supabase
					.from("schools")
					.select("id, name, district")
					.in("id", schoolChunk)
					.order("name");

				if (error) throw error;
				schoolsData.push(...(data || []));
			}

			const schoolMap = new Map(
				schoolsData.map((school) => [school.id, school])
			);

			const teachersData = [];
			for (const schoolChunk of schoolChunks) {
				const { data, error } = await supabase
					.from("profiles")
					.select("id, full_name, email, school_id, avatar_url, status")
					.eq("role", "ROLE_SCHOOL_TEACHER")
					.eq("status", "Active")
					.in("school_id", schoolChunk);

				if (error) throw error;
				teachersData.push(...(data || []));
			}

			const teacherIds = teachersData.map((teacher) => teacher.id);

			const [
				{ data: categoriesData, error: categoriesError },
				{ data: gradesData, error: gradesError },
				{ count: totalOnboardingVideos, error: videosError },
			] = await Promise.all([
				supabase.from("course_categories").select("*").eq("status", true),
				supabase.from("grades").select("*").order("name"),
				supabase
					.from("onboarding_videos")
					.select("id", { count: "exact", head: true }),
			]);

			if (categoriesError) throw categoriesError;
			if (gradesError) throw gradesError;
			if (videosError) throw videosError;

			let progressData = [];
			if (teacherIds.length > 0) {
				for (const teacherChunk of chunkArray(teacherIds)) {
					const { data, error } = await supabase
						.from("teacher_course_progress")
						.select("*")
						.in("teacher_id", teacherChunk)
						.eq("academic_year", selectedAcademicYear)
						.order("last_accessed_at", { ascending: false });

					if (error) throw error;
					progressData = progressData.concat(data || []);
				}
			}

			const completedOnboardingByTeacher = new Map();
			if (teacherIds.length > 0) {
				for (const teacherChunk of chunkArray(teacherIds)) {
					const { data, error } = await supabase
						.from("teacher_onboarding_progress")
						.select("teacher_id")
						.in("teacher_id", teacherChunk)
						.eq("status", "completed");

					if (error) throw error;
					(data || []).forEach((row) => {
						completedOnboardingByTeacher.set(
							row.teacher_id,
							(completedOnboardingByTeacher.get(row.teacher_id) || 0) + 1
						);
					});
				}
			}

			const gradesObj = {};
			gradesData.forEach((grade) => {
				gradesObj[grade.id] = grade;
			});

			const teachersWithOnboarding = teachersData.map((teacher) => ({
				...teacher,
				schools: schoolMap.get(teacher.school_id) || null,
				onboardingProgress: {
					completed: completedOnboardingByTeacher.get(teacher.id) || 0,
					total: totalOnboardingVideos || 0,
				},
			}));

			setTeacherData(teachersWithOnboarding);
			setCourseProgress(progressData);
			setCategories(categoriesData || []);
			setGrades(gradesObj);
			setSchools(schoolsData);
		} catch (error) {
			console.error("Error fetching admin report data:", error);
			setFetchError(
				error?.message || "Failed to load report data. Please refresh the page."
			);
			setTeacherData([]);
			setCourseProgress([]);
			setSchools([]);
		} finally {
			setLoading(false);
		}
	};

	const fetchTeacherMessages = async (teacherId) => {
		if (teacherMessages[teacherId] || loadingMessages[teacherId]) {
			return; // Already loaded or loading
		}

		setLoadingMessages((prev) => ({ ...prev, [teacherId]: true }));

		try {
			const { data: messages, error } = await supabase
				.from("notifications")
				.select(
					`
					id,
					subject,
					message,
					type,
					is_read,
					read_at,
					created_at,
					sender:sender_id (
						full_name,
						email,
						avatar_url
					)
				`
				)
				.eq("recipient_id", teacherId)
				.order("created_at", { ascending: false })
				.limit(20); // Limit to recent 20 messages

			if (error) throw error;

			setTeacherMessages((prev) => ({
				...prev,
				[teacherId]: messages || [],
			}));
		} catch (error) {
			console.error("Error fetching teacher messages:", error);
			setTeacherMessages((prev) => ({
				...prev,
				[teacherId]: [],
			}));
		} finally {
			setLoadingMessages((prev) => ({ ...prev, [teacherId]: false }));
		}
	};

	const getProgressForTeacher = (teacherId) => {
		return courseProgress.filter(
			(progress) => progress.teacher_id === teacherId
		);
	};

	const getCategoryName = (categoryId) => {
		const category = categories.find((cat) => cat.id === categoryId);
		return category?.name || "Unknown Course";
	};

	const getGradeName = (gradeId) => {
		return grades[gradeId]?.name || `Grade ${gradeId}`;
	};

	const getTeacherStats = (teacherId) => {
		const progress = getProgressForTeacher(teacherId);
		const completed = progress.filter((p) => p.completion_percentage === 100);
		const inProgress = progress.filter(
			(p) => p.completion_percentage > 0 && p.completion_percentage < 100
		);

		return {
			total: progress.length,
			completed: completed.length,
			inProgress: inProgress.length,
			averageProgress:
				progress.length > 0
					? Math.round(
							progress.reduce(
								(sum, p) => sum + (p.completion_percentage || 0),
								0
							) / progress.length
					  )
					: 0,
		};
	};

	// Enhanced search function that searches all content including expanded data
	const searchAllContent = (teacher, query) => {
		const lowerQuery = query.toLowerCase();

		// Search basic teacher info
		const basicFields = [
			teacher.full_name,
			teacher.email,
			teacher.schools?.name,
			teacher.schools?.district,
		].filter(Boolean);

		const basicMatch = basicFields.some((field) =>
			field.toLowerCase().includes(lowerQuery)
		);

		if (basicMatch) return true;

		// Search onboarding progress
		if (teacher.onboardingProgress) {
			const onboardingText = `${teacher.onboardingProgress.completed}/${teacher.onboardingProgress.total} onboarding videos`;
			if (onboardingText.toLowerCase().includes(lowerQuery)) return true;
		}

		// Search teacher progress data
		const teacherProgress = getProgressForTeacher(teacher.id);
		const progressMatch = teacherProgress.some((progress) => {
			const categoryName = getCategoryName(progress.category_id);
			const gradeName = getGradeName(progress.grade_id);
			const status =
				progress.completion_percentage === 100
					? "completed"
					: progress.completion_percentage > 0
					? "in progress"
					: "not started";

			return [categoryName, gradeName, status].some((field) =>
				field.toLowerCase().includes(lowerQuery)
			);
		});

		if (progressMatch) return true;

		// Search teacher messages if loaded
		const messages = teacherMessages[teacher.id];
		if (messages) {
			const messageMatch = messages.some((message) => {
				const messageFields = [
					message.subject,
					message.message,
					message.sender?.full_name,
					message.sender?.email,
					message.is_read ? "read" : "unread",
					message.type,
				].filter(Boolean);

				return messageFields.some((field) =>
					field.toLowerCase().includes(lowerQuery)
				);
			});

			if (messageMatch) return true;
		}

		return false;
	};

	// Filtered teacher data based on search and school filter
	const filteredTeacherData = useMemo(() => {
		let filtered = teacherData;

		// Apply school filter
		if (selectedSchool !== "all") {
			filtered = filtered.filter(
				(teacher) => teacher.school_id === selectedSchool
			);
		}

		// Apply search filter
		if (searchQuery.trim()) {
			filtered = filtered.filter((teacher) =>
				searchAllContent(teacher, searchQuery.trim())
			);
		}

		return filtered;
	}, [
		teacherData,
		selectedSchool,
		searchQuery,
		courseProgress,
		categories,
		grades,
		teacherMessages,
	]);

	const handleExpandRow = (teacherId) => {
		const newExpanded = new Set(expandedRows);
		if (newExpanded.has(teacherId)) {
			newExpanded.delete(teacherId);
		} else {
			newExpanded.add(teacherId);
			// Initialize tab to 0 (Game Progress) when expanding
			setExpandedTabValues((prev) => ({ ...prev, [teacherId]: 0 }));
		}
		setExpandedRows(newExpanded);
	};

	const handleTabChange = (teacherId, newValue) => {
		setExpandedTabValues((prev) => ({ ...prev, [teacherId]: newValue }));

		// Fetch messages when Messages tab is selected
		if (newValue === 1) {
			fetchTeacherMessages(teacherId);
		}
	};

	const handleSendTeacherNotification = (teacher) => {
		setSelectedRecipient(teacher);
		setOpenNotificationModal(true);
	};

	const handleCloseNotificationModal = () => {
		setOpenNotificationModal(false);
		setSelectedRecipient(null);
	};

	const formatMessageDate = (dateString) => {
		return dayjs(dateString).fromNow();
	};

	const getMessageIcon = (message) => {
		return message.is_read ? (
			<MailOpen size={16} color="#6b7280" />
		) : (
			<Mail size={16} color="#3b82f6" />
		);
	};

	// Helper function to get page title based on user role
	const getPageTitle = () => {
		if (userProfile?.role === "ROLE_FUNMIYO_EMPLOYEE") {
			return "Funmath Teacher Report";
		}
		return "Funmath Admin Report";
	};

	// Clear all filters
	const clearFilters = () => {
		setSearchQuery("");
		setSelectedSchool("all");
	};

	if (loading) {
		return (
			<Box sx={{ p: 3 }}>
				<Skeleton variant="text" height={40} width="40%" sx={{ mb: 3 }} />
				<Paper sx={{ p: 3 }}>
					<Skeleton variant="rectangular" height={400} />
				</Paper>
			</Box>
		);
	}

	if (!userProfile || !ALLOWED_ROLES.includes(userProfile.role)) {
		return (
			<Box sx={{ p: 3 }}>
				<Alert severity="error">
					Access denied. You must be a Funmath Admin or Funmiyo Employee to view
					this report.
				</Alert>
			</Box>
		);
	}

	if (userSchoolIds.length === 0) {
		return (
			<Box sx={{ p: 3 }}>
				<Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
					{getPageTitle()}
				</Typography>
				<Alert severity="info" sx={{ mt: 2 }}>
					No schools are currently assigned to your account. Please contact your
					administrator to assign schools to view reports.
				</Alert>
			</Box>
		);
	}

	return (
		<Box sx={{ p: 3 }}>
			{fetchError && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{fetchError}
				</Alert>
			)}

			{/* Header with Send Notifications Button */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 3,
				}}>
				<Typography variant="h4" sx={{ fontWeight: 600 }}>
					{getPageTitle()}
				</Typography>
				<Button
					variant="contained"
					startIcon={<Send size={20} />}
					onClick={() => {
						setSelectedRecipient(null);
						setOpenNotificationModal(true);
					}}
					sx={{
						bgcolor: "primary.main",
						"&:hover": {
							bgcolor: "primary.dark",
						},
					}}>
					Send Notifications
				</Button>
			</Box>

			{/* Filters Section */}
			<Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
				<Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
					<Filter size={20} />
					<Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
						Filters
					</Typography>
				</Box>
				<Grid container spacing={2} alignItems="center">
					<Grid item xs={12} sm={6} md={4}>
						<TextField
							fullWidth
							size="small"
							placeholder="Search teachers, schools, games, messages..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Search size={16} color="#64748b" />
									</InputAdornment>
								),
							}}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<FormControl fullWidth size="small">
							<InputLabel>School</InputLabel>
							<Select
								value={selectedSchool}
								label="School"
								onChange={(e) => setSelectedSchool(e.target.value)}>
								<MenuItem value="all">All Schools</MenuItem>
								{schools.map((school) => (
									<MenuItem key={school.id} value={school.id}>
										{school.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<AcademicYearSelector
							value={selectedAcademicYear}
							onChange={setSelectedAcademicYear}
							sx={{ width: "100%" }}
							minWidth={undefined}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2}>
						<Button
							variant="outlined"
							onClick={clearFilters}
							fullWidth
							sx={{ height: "40px" }}>
							Clear Filters
						</Button>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Typography variant="body2" color="text.secondary">
							Showing {filteredTeacherData.length} of {teacherData.length}{" "}
							teachers
						</Typography>
					</Grid>
				</Grid>
			</Paper>

			{/* Main Table */}
			<Paper sx={{ width: "100%", overflow: "hidden" }}>
				<TableContainer>
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: 600, width: 50 }}></TableCell>
								<TableCell sx={{ fontWeight: 600 }}>School</TableCell>
								<TableCell sx={{ fontWeight: 600 }}>Teacher</TableCell>
								<TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
								<TableCell sx={{ fontWeight: 600, textAlign: "center" }}>
									Onboarding Videos
								</TableCell>
								<TableCell sx={{ fontWeight: 600, textAlign: "center" }}>
									Courses
								</TableCell>
								<TableCell sx={{ fontWeight: 600, textAlign: "center" }}>
									Completed
								</TableCell>
								<TableCell sx={{ fontWeight: 600, textAlign: "center" }}>
									In Progress
								</TableCell>
								<TableCell sx={{ fontWeight: 600, textAlign: "center" }}>
									Avg Progress
								</TableCell>
								<TableCell sx={{ fontWeight: 600, textAlign: "center" }}>
									Actions
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{filteredTeacherData.map((teacher) => {
								const stats = getTeacherStats(teacher.id);
								const isExpanded = expandedRows.has(teacher.id);
								const teacherProgress = getProgressForTeacher(teacher.id);
								const currentTabValue = expandedTabValues[teacher.id] || 0;

								return (
									<React.Fragment key={teacher.id}>
										{/* Main Row */}
										<TableRow hover>
											<TableCell>
												<IconButton
													size="small"
													onClick={() => handleExpandRow(teacher.id)}
													disabled={teacherProgress.length === 0}>
													{teacherProgress.length > 0 ? (
														isExpanded ? (
															<ChevronUp />
														) : (
															<ChevronDown />
														)
													) : null}
												</IconButton>
											</TableCell>
											<TableCell>
												<Box>
													<Typography variant="body2" sx={{ fontWeight: 500 }}>
														{teacher.schools?.name}
													</Typography>
													{teacher.schools?.district && (
														<Typography
															variant="caption"
															color="text.secondary">
															{teacher.schools.district}
														</Typography>
													)}
												</Box>
											</TableCell>
											<TableCell>
												<Box sx={{ display: "flex", alignItems: "center" }}>
													<Avatar
														src={teacher.avatar_url}
														sx={{ width: 32, height: 32, mr: 2 }}>
														<User size={16} />
													</Avatar>
													<Typography variant="body2">
														{teacher.full_name || "Unknown"}
													</Typography>
												</Box>
											</TableCell>
											<TableCell>
												<Typography variant="body2" color="text.secondary">
													{teacher.email}
												</Typography>
											</TableCell>
											<TableCell align="center">
												{teacher.onboardingProgress ? (
													<Chip
														label={`${teacher.onboardingProgress.completed}/${teacher.onboardingProgress.total}`}
														size="small"
														color={
															teacher.onboardingProgress.completed ===
																teacher.onboardingProgress.total &&
															teacher.onboardingProgress.total > 0
																? "success"
																: teacher.onboardingProgress.completed > 0
																? "warning"
																: "default"
														}
														variant="outlined"
													/>
												) : (
													<Typography variant="body2" color="text.secondary">
														-
													</Typography>
												)}
											</TableCell>
											<TableCell align="center">
												<Chip label={stats.total} size="small" color="info" />
											</TableCell>
											<TableCell align="center">
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
													}}>
													<CheckCircle size={16} className="text-green-600" />
													<Typography variant="body2" sx={{ ml: 1 }}>
														{stats.completed}
													</Typography>
												</Box>
											</TableCell>
											<TableCell align="center">
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
													}}>
													<Clock size={16} className="text-blue-600" />
													<Typography variant="body2" sx={{ ml: 1 }}>
														{stats.inProgress}
													</Typography>
												</Box>
											</TableCell>
											<TableCell align="center">
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														minWidth: 100,
													}}>
													<LinearProgress
														variant="determinate"
														value={stats.averageProgress}
														sx={{ flex: 1, mr: 1, height: 6, borderRadius: 1 }}
													/>
													<Typography variant="caption">
														{stats.averageProgress}%
													</Typography>
												</Box>
											</TableCell>
											<TableCell align="center">
												<Tooltip title="Send notification">
													<IconButton
														size="small"
														onClick={() =>
															handleSendTeacherNotification(teacher)
														}
														sx={{
															color: "primary.main",
															"&:hover": { bgcolor: "primary.50" },
														}}>
														<Send size={16} />
													</IconButton>
												</Tooltip>
											</TableCell>
										</TableRow>

										{/* Expanded Row - Tabbed Content */}
										<TableRow>
											<TableCell
												style={{ paddingBottom: 0, paddingTop: 0 }}
												colSpan={10}>
												<Collapse in={isExpanded} timeout="auto" unmountOnExit>
													<Box sx={{ margin: 2 }}>
														{/* Tabs */}
														<Tabs
															value={currentTabValue}
															onChange={(event, newValue) =>
																handleTabChange(teacher.id, newValue)
															}
															sx={{
																borderBottom: 1,
																borderColor: "divider",
																mb: 2,
															}}>
															<Tab
																label="Game Progress"
																icon={<PlayCircle size={16} />}
																iconPosition="start"
															/>
															<Tab
																label="Messages"
																icon={<MessageSquare size={16} />}
																iconPosition="start"
															/>
														</Tabs>

														{/* Tab Content */}
														{currentTabValue === 0 && (
															<Box>
																<Typography
																	variant="h6"
																	gutterBottom
																	component="div"
																	sx={{ fontWeight: 600 }}>
																	Game Progress Details
																</Typography>
																{teacherProgress.length === 0 ? (
																	<Typography
																		variant="body2"
																		color="text.secondary">
																		No game progress found for this teacher.
																	</Typography>
																) : (
																	<Table size="small">
																		<TableHead>
																			<TableRow>
																				<TableCell sx={{ fontWeight: 600 }}>
																					Game Category
																				</TableCell>
																				<TableCell sx={{ fontWeight: 600 }}>
																					Grade
																				</TableCell>
																				<TableCell sx={{ fontWeight: 600 }}>
																					Progress
																				</TableCell>
																				<TableCell sx={{ fontWeight: 600 }}>
																					Status
																				</TableCell>
																				<TableCell sx={{ fontWeight: 600 }}>
																					Last Accessed
																				</TableCell>
																			</TableRow>
																		</TableHead>
																		<TableBody>
																			{teacherProgress.map((progress) => (
																				<TableRow key={progress.id}>
																					<TableCell>
																						<Box
																							sx={{
																								display: "flex",
																								alignItems: "center",
																							}}>
																							<PlayCircle
																								size={16}
																								className="text-purple-600"
																							/>
																							<Typography
																								variant="body2"
																								sx={{ ml: 1 }}>
																								{getCategoryName(
																									progress.category_id
																								)}
																							</Typography>
																						</Box>
																					</TableCell>
																					<TableCell>
																						<Chip
																							label={getGradeName(
																								progress.grade_id
																							)}
																							size="small"
																							variant="outlined"
																						/>
																					</TableCell>
																					<TableCell>
																						<Box
																							sx={{
																								display: "flex",
																								alignItems: "center",
																								minWidth: 120,
																							}}>
																							<LinearProgress
																								variant="determinate"
																								value={
																									progress.completion_percentage ||
																									0
																								}
																								sx={{
																									flex: 1,
																									mr: 1,
																									height: 4,
																									borderRadius: 1,
																								}}
																							/>
																							<Typography variant="caption">
																								{progress.completion_percentage ||
																									0}
																								%
																							</Typography>
																						</Box>
																					</TableCell>
																					<TableCell>
																						{progress.completion_percentage ===
																						100 ? (
																							<Chip
																								icon={<Trophy size={12} />}
																								label="Completed"
																								size="small"
																								color="success"
																							/>
																						) : progress.completion_percentage >
																						  0 ? (
																							<Chip
																								icon={<Target size={12} />}
																								label="In Progress"
																								size="small"
																								color="primary"
																							/>
																						) : (
																							<Chip
																								label="Not Started"
																								size="small"
																								variant="outlined"
																							/>
																						)}
																					</TableCell>
																					<TableCell>
																						<Typography
																							variant="caption"
																							color="text.secondary">
																							{progress.last_accessed_at
																								? new Date(
																										progress.last_accessed_at
																								  ).toLocaleDateString()
																								: "Never"}
																						</Typography>
																					</TableCell>
																				</TableRow>
																			))}
																		</TableBody>
																	</Table>
																)}
															</Box>
														)}

														{currentTabValue === 1 && (
															<Box>
																<Typography
																	variant="h6"
																	gutterBottom
																	component="div"
																	sx={{ fontWeight: 600 }}>
																	Messages & Notifications
																</Typography>

																{loadingMessages[teacher.id] ? (
																	<Box
																		sx={{
																			display: "flex",
																			justifyContent: "center",
																			p: 2,
																		}}>
																		<Stack
																			direction="row"
																			spacing={1}
																			alignItems="center">
																			<Box sx={{ width: 20, height: 20 }}>
																				<LinearProgress />
																			</Box>
																			<Typography
																				variant="body2"
																				color="text.secondary">
																				Loading messages...
																			</Typography>
																		</Stack>
																	</Box>
																) : !teacherMessages[teacher.id] ||
																  teacherMessages[teacher.id].length === 0 ? (
																	<Box sx={{ textAlign: "center", py: 3 }}>
																		<MessageSquare size={48} color="#9ca3af" />
																		<Typography
																			variant="body2"
																			color="text.secondary"
																			sx={{ mt: 1 }}>
																			No messages found for this teacher
																		</Typography>
																	</Box>
																) : (
																	<List
																		sx={{ maxHeight: 400, overflow: "auto" }}>
																		{teacherMessages[teacher.id].map(
																			(message, index) => (
																				<React.Fragment key={message.id}>
																					<ListItem
																						sx={{
																							px: 0,
																							py: 1.5,
																							bgcolor: message.is_read
																								? "transparent"
																								: "rgba(59, 130, 246, 0.05)",
																						}}>
																						<ListItemIcon sx={{ minWidth: 40 }}>
																							{getMessageIcon(message)}
																						</ListItemIcon>
																						<ListItemText
																							primary={
																								<Box
																									sx={{
																										display: "flex",
																										justifyContent:
																											"space-between",
																										alignItems: "flex-start",
																										mb: 0.5,
																									}}>
																									<Typography
																										variant="subtitle2"
																										sx={{
																											fontWeight:
																												message.is_read
																													? "normal"
																													: "bold",
																											color: message.is_read
																												? "text.secondary"
																												: "text.primary",
																										}}>
																										{message.subject}
																									</Typography>
																									<Typography
																										variant="caption"
																										color="text.secondary">
																										{formatMessageDate(
																											message.created_at
																										)}
																									</Typography>
																								</Box>
																							}
																							secondary={
																								<Box>
																									<Typography
																										variant="body2"
																										color="text.secondary"
																										sx={{ mb: 1 }}>
																										{message.message}
																									</Typography>
																									{message.sender && (
																										<Box
																											sx={{
																												display: "flex",
																												alignItems: "center",
																												gap: 1,
																											}}>
																											<Avatar
																												src={
																													message.sender
																														.avatar_url
																												}
																												sx={{
																													width: 20,
																													height: 20,
																												}}>
																												<User size={12} />
																											</Avatar>
																											<Typography
																												variant="caption"
																												color="text.secondary">
																												From:{" "}
																												{
																													message.sender
																														.full_name
																												}
																											</Typography>
																										</Box>
																									)}
																									{message.read_at && (
																										<Box
																											sx={{
																												display: "flex",
																												alignItems: "center",
																												gap: 0.5,
																												mt: 0.5,
																											}}>
																											<Calendar
																												size={12}
																												color="#6b7280"
																											/>
																											<Typography
																												variant="caption"
																												color="text.secondary">
																												Read:{" "}
																												{formatMessageDate(
																													message.read_at
																												)}
																											</Typography>
																										</Box>
																									)}
																								</Box>
																							}
																						/>
																					</ListItem>
																					{index <
																						teacherMessages[teacher.id].length -
																							1 && <Divider />}
																				</React.Fragment>
																			)
																		)}
																	</List>
																)}
															</Box>
														)}
													</Box>
												</Collapse>
											</TableCell>
										</TableRow>
									</React.Fragment>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>

				{filteredTeacherData.length === 0 && (
					<Box sx={{ textAlign: "center", py: 4 }}>
						<Typography variant="body1" color="text.secondary">
							{teacherData.length === 0
								? "No teachers found in your assigned schools."
								: "No teachers match the current filters."}
						</Typography>
						{searchQuery || selectedSchool !== "all" ? (
							<Button variant="outlined" onClick={clearFilters} sx={{ mt: 2 }}>
								Clear Filters
							</Button>
						) : null}
					</Box>
				)}
			</Paper>

			{/* Send Notification Modal */}
			{userProfile && (
				<SendNotificationModal
					open={openNotificationModal}
					onClose={handleCloseNotificationModal}
					supabase={supabase}
					currentUser={userProfile}
					preselectedRecipient={selectedRecipient}
					userSchoolIds={userSchoolIds}
				/>
			)}
		</Box>
	);
};

export default FunmathAdminReport;
